import {
  loginUser,
  userRefreshToken,
  setAuthToken,
} from "../../util/session_api_util";
import jwt_decode from "jwt-decode";
import {updateUserIdleStatus} from "../../util/user_util";
import { online } from "../../util/user_util";

export const RECEIVE_CURRENT_USER = "RECEIVE_CURRENT_USER";
export const RECEIVE_SESSION_ERRORS = "RECEIVE_SESSION_ERRORS";
export const RECEIVE_USER_LOGOUT = "RECEIVE_USER_LOGOUT";
export const CLEAR_SESSION_ERRORS = "CLEAR_SESSION_ERRORS";
export const RECEIVE_REFRESHED_SESSION = "RECEIVE_REFRESHED_SESSION";

// Updated to accept refreshTokenExp
export const receiveCurrentUser = (currentUser, refreshTokenExp) => ({
  type: RECEIVE_CURRENT_USER,
  currentUser,
  refreshTokenExp, // Add refreshTokenExp to the action payload
});

// We dispatch this one to show authentication errors on the frontend
export const receiveErrors = (errors) => ({
  type: RECEIVE_SESSION_ERRORS,
  errors,
});

// When our user is logged out, we will dispatch this action to set isAuthenticated to false
export const logoutUser = () => ({
  type: RECEIVE_USER_LOGOUT,
});

// Thunk action to check refresh token validity and refresh if needed
export const checkAndRefreshIfNeeded = () => async (dispatch, getState) => {
  const state = getState();
  const { session } = state;
  const now = Date.now();

  console.log("[Thunk checkAndRefreshIfNeeded] Running check...");

  if (!session.isAuthenticated) {
    console.log("[Thunk checkAndRefreshIfNeeded] Not authenticated. Skipping refresh check.");
    return; 
  }

  const isRefreshTokenValid = () => {
    if (session.refreshTokenExp) {
      const refreshTokenExpiryTime = session.refreshTokenExp * 1000;
      const refreshBufferMs = 2000; // 5 second buffer
      const isValid = refreshTokenExpiryTime > (now + refreshBufferMs);
      console.log(`[Thunk checkAndRefreshIfNeeded] Refresh token check: Expires ${new Date(refreshTokenExpiryTime)}, Now+Buffer ${new Date(now + refreshBufferMs)}, Valid: ${isValid}`);
      return isValid;
    } else {
      console.warn("[Thunk checkAndRefreshIfNeeded] Refresh token expiry time not found in session state.");
      return false;
    }
  };

  const storedRefreshToken = localStorage.getItem('refreshToken');

  if (storedRefreshToken && isRefreshTokenValid()) {
    console.log("[Thunk checkAndRefreshIfNeeded] Refresh token is valid. Dispatching refreshUser...");
    dispatch(refreshUser(storedRefreshToken)); 
  } else {
    console.warn("[Thunk checkAndRefreshIfNeeded] Conditions not met for refresh (token missing, expired, or invalid).");
    // Check backend reachability before deciding how to log out
    const isBackendReachable = await online();
    if (isBackendReachable) {
        // ONLINE: Logout *and* clear cache (use default options)
        if (session.isAuthenticated) { 
            console.log("[Thunk checkAndRefreshIfNeeded] Refresh token invalid/expired/missing AND ONLINE. Dispatching logout WITH cache clear.");
            dispatch(logout(session.user?._id)); // Call without options to use default clearCache: true
        }
    } else {
        // OFFLINE: Don't log out, allow offline use
        console.log("[Thunk checkAndRefreshIfNeeded] Refresh token invalid/expired/missing BUT OFFLINE. Allowing offline session to continue.");
    }
  }
};

// Upon login, set the session token and dispatch the current user. Dispatch errors on failure.
export const login = (user) => (dispatch) =>
  loginUser(user)
    .then((res) => {
      // Electron-only guard: if offline or no token, proceed without decoding/storing tokens
      const isElectron = !!(typeof window !== 'undefined' && window.electronAPI && window.electronAPI.isElectron);
      const { accessToken, refreshToken, offline, user: offlineUser } = res.data || {};
      console.log("res.datasdsdsds", res.data);
      if (isElectron && (offline || !accessToken)) {
        // Allow offline/tokenless session in Electron
        dispatch(receiveCurrentUser(offlineUser || {}, null));
        return res;
      }

      // Web app or Electron online with tokens: proceed as before
      localStorage.setItem("jwtToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      console.log(`Logged in. Access & refresh tokens storsed.`);
      setAuthToken(accessToken);

      let decodedAccess = null;
      let refreshTokenExp = null;

      try {
        decodedAccess = jwt_decode(accessToken);
      } catch (e) {
        console.error("Failed to decode access token on login:", e);
        dispatch(receiveErrors({ message: "Invalid access token received on login." }));
        return Promise.reject(new Error("Invalid access token"));
      }

      try {
        if (refreshToken) {
          const decodedRefresh = jwt_decode(refreshToken);
          refreshTokenExp = decodedRefresh.exp;
          console.log("Decoded Refresh Token Expiry on login:", refreshTokenExp);
        }
      } catch (e) {
        console.error("Failed to decode refresh token on login:", e);
        // Log error but proceed
      }

      // Dispatch with decoded user and refresh token expiry
      dispatch(receiveCurrentUser(decodedAccess, refreshTokenExp));
      return res;
    })
    .catch((err) => {
      dispatch(receiveErrors(err.response ? err.response.data : err));
      return Promise.reject(err); // Ensure promise chain rejects on error
    });

// Upon refresh request, set the new access token and update expiry times. Dispatch errors on failure.
export const refreshUser = (token) => (dispatch, getState) => // Ensure getState is available if needed
  userRefreshToken(token)
    .then((res) => {
      // Destructure BOTH the new access token and the new refresh token from the response
      let { accessToken, refreshToken } = res.data;

      console.log("Refresh Response - Access Token:", accessToken);
      console.log("Refresh Response - Refresh Token:", refreshToken); // Log the new refresh token

      // Store BOTH the new access token and the new refresh token
      localStorage.setItem("jwtToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);

      setAuthToken(accessToken);

      let decodedAccess = null;
      let newRefreshTokenExp = null; // Variable for the new refresh token expiry

      try {
        decodedAccess = jwt_decode(accessToken);
        console.log("Decoded Access Token after refresh:", decodedAccess);
      } catch (e) {
        console.error("Failed to decode access token after refresh:", e);
        dispatch(receiveErrors({ message: "Invalid access token received after refresh." }));
        return Promise.reject(new Error("Invalid access token after refresh"));
      }

      // Decode the NEW refresh token to get its expiry
      try {
        if (refreshToken) { // Use the refreshToken received from the response
          const decodedRefresh = jwt_decode(refreshToken);
          newRefreshTokenExp = decodedRefresh.exp;
          console.log("Using NEW Refresh Token Expiry from response:", newRefreshTokenExp);
        } else {
          console.warn("No new refresh token found in response after refresh attempt.");
        }
      } catch (e) {
        console.error("Failed to decode new refresh token after refresh attempt:", e);
        // Log error but proceed with potentially null newRefreshTokenExp
      }

      // Dispatch with decoded access token and the NEW refresh token expiry
      if (decodedAccess) {
        dispatch(receiveCurrentUser(decodedAccess, newRefreshTokenExp)); // Pass the new expiry
      }
      return res;
    })
    .catch(async (err) => {
      // Check specifically if the error indicates the refresh token we sent was invalid or expired
      const isBackendReachable = await online();
      if (
        isBackendReachable &&
        err?.response?.status === 401 && // Check for 401 Unauthorized
        err.response?.data?.error === "Invalid or expired refresh token!"
      ) {
        console.warn("Refresh token invalid or expired. Forcing logout.");
        dispatch(logout()); // Trigger logout action
      } else {
        // Handle other network errors or unexpected backend issues
        console.error("An error occurred during token refresh:", err);
        const errorData = err?.response?.data || { message: "An unexpected error occurred during token refresh." };
        dispatch(receiveErrors(errorData));
      }
      // Ensure the promise chain rejects on error
      return Promise.reject(err);
    });

export const logout = (userID, options = { clearCache: true }) => (dispatch) => {
  console.log(`LOGOUT action called for userID: ${userID}. Options:`, options);
  localStorage.removeItem("jwtToken");
  localStorage.removeItem("refreshToken");
  
  // Conditionally clear cache based on options
  if (options.clearCache) {
    console.log("Clear cache option is true. Sending message to SW...");
    // --- START: Clear Runtime Cache via Service Worker ---
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      console.log('Sending message to SW to clear runtime cache.');
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_RUNTIME_CACHE' });
    } else {
      console.warn('Service worker controller not found. Cannot clear runtime cache via SW.');
    }
    // --- END: Clear Runtime Cache via Service Worker ---
  } else {
      console.log("Clear cache option is false. Skipping SW message.");
  }

  // Update user status (can proceed regardless of cache clearing)
  updateUserIdleStatus("Offline", userID).then(res =>{
      setAuthToken(false); // Clear auth header
      dispatch(logoutUser()); // Dispatch the Redux state update
      console.log("User status updated, auth token cleared, logoutUser dispatched.");
  }).catch(err => {
      // Handle potential error during status update, but still logout state
      console.error("Error updating user status during logout:", err);
      setAuthToken(false);
      dispatch(logoutUser());
  });
};

// Action creator for session refreshed via interceptor
export const receiveRefreshedSession = (accessToken, refreshTokenExp) => {
    try {
        const decodedUser = jwt_decode(accessToken);
        return {
            type: RECEIVE_REFRESHED_SESSION,
            payload: {
                user: decodedUser, // Decoded user from new access token
                refreshTokenExp // Existing refresh token expiry
            }
        };
    } catch (e) {
        // Handle potential decode error, maybe return a different action or log
        console.error("[receiveRefreshedSession] Failed to decode access token:", e);
        // Optionally return an error action or logout action here
        return logoutUser(); // Example: Trigger logout if decode fails
    }
};
